import { useMemo, useState } from 'react';
import type { ClientPhoto, PhotoKind, PhotoVisibility } from '@salonomy/types';
import { Button, Pill, Select, Spinner } from '@salonomy/ui';
import {
  useCapturePhotoConsent,
  useClientPhotos,
  useDeletePhoto,
  useUpdatePhoto,
} from '../../data/photos.js';
import { ApiClientError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { useToast } from '../../lib/toast.js';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import { PhotoUploadModal } from './PhotoUploadModal.js';
import { dateFormat } from '../../lib/locale.js';
import {
  groupIntoSets,
  isStorageDisabledError,
  VISIBILITY_HINT,
  VISIBILITY_LABEL,
  visibilityTone,
  type PhotoSet,
} from './photo-shared.js';

const DATE_FMT = dateFormat({
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/** What the parent wants to capture next: a fresh set's before, or the after of an existing set. */
type UploadTarget =
  | { mode: 'new'; kind: 'before' }
  | { mode: 'attach'; pairId: string; kind: PhotoKind; title: string };

/**
 * WS-13 Feature 5 — the BEFORE/AFTER PHOTOS gallery on the client record. Shows the client's photos grouped
 * into before/after SETS (by pairId) using the server-minted signed downloadUrls, with:
 *   - a CAPTURE/UPLOAD flow (file/camera → init → presigned PUT → confirm);
 *   - a per-set VISIBILITY selector (salon_private / client_visible / showcase) where SHOWCASE is disabled
 *     until portfolio CONSENT is recorded for the set AND the signed-in user is a manager;
 *   - a distinct "record portfolio consent" action (NOT messaging consent);
 *   - a typed-confirm DELETE per photo.
 *
 * PRIVACY: this gallery is staff-only and shows ONE client's photos — the data hook reads /v1/clients/:id/
 * photos which the API gates to staff who can see the client (it NEVER returns another client's photos), and
 * the downloadUrls are short-lived signed URLs minted only after that server-side check.
 *
 * STORAGE-DISABLED HONESTY (the staging posture): the image bytes live in an S3 bucket that is INERT until
 * real creds exist. The init route returns 503 in that posture; when we see it (either proactively, if a
 * list read ever signalled it, or reactively when an upload attempt 503s) we render "Photo storage isn’t
 * configured yet" instead of a broken uploader.
 */
export function PhotoGallery({ clientId, clientName }: { clientId: string; clientName: string }) {
  const { isManager } = useAuth();
  const { data, isLoading, isError, error } = useClientPhotos(clientId);
  const [storageDisabled, setStorageDisabled] = useState(false);
  const [upload, setUpload] = useState<UploadTarget | null>(null);
  const [deleting, setDeleting] = useState<ClientPhoto | null>(null);

  const sets = useMemo(() => groupIntoSets(data ?? []), [data]);

  // If the list read itself fails with a 503/disabled signal, treat storage as not configured (honest).
  const listSaysDisabled = isError && isStorageDisabledError(error);
  const disabled = storageDisabled || listSaysDisabled;

  if (isLoading) {
    return (
      <div className="client-detail__block">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Spinner size={14} />
          <span className="muted" style={{ fontSize: 12.5 }}>
            Loading photos…
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="client-detail__block">
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
      >
        <span className="slm-stat__label">Before &amp; after photos</span>
        {!disabled ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setUpload({ mode: 'new', kind: 'before' })}
          >
            New set
          </Button>
        ) : null}
      </div>

      {disabled ? (
        <div className="muted" style={{ fontSize: 12.5, marginTop: 10, lineHeight: 1.5 }}>
          Photo storage isn’t configured yet. Once your salon’s photo storage is set up you’ll be
          able to capture before &amp; after sets here.
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
          Couldn’t load this client’s photos.
        </div>
      ) : sets.length === 0 ? (
        <div className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
          No before &amp; after photos yet. Capture a “before”, then add the matching “after”.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 12 }}>
          {sets.map((set) => (
            <SetRow
              key={set.pairId}
              clientId={clientId}
              set={set}
              isManager={isManager}
              onAddAfter={() =>
                setUpload({
                  mode: 'attach',
                  pairId: set.pairId,
                  kind: 'after',
                  title: 'Add the “after” photo',
                })
              }
              onAddBefore={() =>
                setUpload({
                  mode: 'attach',
                  pairId: set.pairId,
                  kind: 'before',
                  title: 'Add the “before” photo',
                })
              }
              onDelete={(p) => setDeleting(p)}
              onStorageDisabled={() => setStorageDisabled(true)}
            />
          ))}
        </div>
      )}

      {upload ? (
        <PhotoUploadModal
          clientId={clientId}
          pairId={upload.mode === 'attach' ? upload.pairId : undefined}
          kind={upload.kind}
          title={upload.mode === 'attach' ? upload.title : 'Capture a “before” photo (new set)'}
          onClose={() => setUpload(null)}
          onStorageDisabled={() => setStorageDisabled(true)}
        />
      ) : null}

      {deleting ? (
        <DeletePhotoDialog
          clientId={clientId}
          photo={deleting}
          clientName={clientName}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
}

/** One before/after SET: the two thumbnails, the set's visibility/consent controls, and add/delete. */
function SetRow({
  clientId,
  set,
  isManager,
  onAddAfter,
  onAddBefore,
  onDelete,
  onStorageDisabled,
}: {
  clientId: string;
  set: PhotoSet;
  isManager: boolean;
  onAddAfter: () => void;
  onAddBefore: () => void;
  onDelete: (photo: ClientPhoto) => void;
  onStorageDisabled: () => void;
}) {
  const toast = useToast();
  const update = useUpdatePhoto();
  const consent = useCapturePhotoConsent();

  // A visibility change moves the SET as a unit — apply it to whichever halves exist.
  async function changeVisibility(next: PhotoVisibility) {
    const targets = [set.before, set.after].filter((p): p is ClientPhoto => p != null);
    try {
      for (const p of targets) {
        await update.mutateAsync({ clientId, photoId: p.id, body: { visibility: next } });
      }
      toast.success(`Set is now “${VISIBILITY_LABEL[next]}”.`);
    } catch (err) {
      if (isStorageDisabledError(err)) {
        onStorageDisabled();
        return;
      }
      toast.error(err instanceof ApiClientError ? err.message : 'Could not change visibility.');
    }
  }

  async function recordConsent() {
    // Consent is recorded PER PHOTO on the contract; the SET moves as a unit, so stamp every half present.
    const photoIds = [set.before, set.after]
      .filter((p): p is ClientPhoto => p != null)
      .map((p) => p.id);
    if (photoIds.length === 0) return;
    try {
      await consent.mutateAsync({ clientId, photoIds });
      toast.success('Portfolio consent recorded for this set.');
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not record consent.');
    }
  }

  // Showcase is gated: needs portfolio consent recorded AND a manager. We DISABLE the option to match the
  // server's RBAC (which 403/409s otherwise) — the UI never lets a click that the API will reject look valid.
  const showcaseAllowed = set.consentMarketing && isManager;
  const busy = update.isPending || consent.isPending;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        borderBottom: '1px solid var(--slm-border)',
        paddingBottom: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="muted" style={{ fontSize: 12.5 }}>
          {DATE_FMT.format(new Date(set.createdAt))}
        </span>
        <Pill tone={visibilityTone(set.visibility)}>{VISIBILITY_LABEL[set.visibility]}</Pill>
        {set.consentMarketing ? <Pill tone="soft">Portfolio consent</Pill> : null}
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <PhotoSlot label="Before" photo={set.before} onAdd={onAddBefore} onDelete={onDelete} />
        <PhotoSlot label="After" photo={set.after} onAdd={onAddAfter} onDelete={onDelete} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label className="slm-stat__label" htmlFor={`vis-${set.pairId}`}>
          Who can see this set
        </label>
        <Select
          id={`vis-${set.pairId}`}
          value={set.visibility}
          disabled={busy}
          onChange={(e) => void changeVisibility(e.target.value as PhotoVisibility)}
        >
          <option value="salon_private">{VISIBILITY_LABEL.salon_private}</option>
          <option value="client_visible">{VISIBILITY_LABEL.client_visible}</option>
          <option value="showcase" disabled={!showcaseAllowed}>
            {VISIBILITY_LABEL.showcase}
            {!showcaseAllowed
              ? !set.consentMarketing
                ? ' — needs consent'
                : ' — managers only'
              : ''}
          </option>
        </Select>
        <span className="muted" style={{ fontSize: 12 }}>
          {VISIBILITY_HINT[set.visibility]}
        </span>
      </div>

      {/* The DISTINCT portfolio-consent action — separate from messaging consent. Once recorded, a manager
          can promote the set to showcase. Shown until consent exists. */}
      {!set.consentMarketing ? (
        <div>
          <Button
            size="sm"
            variant="outline"
            disabled={consent.isPending}
            onClick={() => void recordConsent()}
          >
            {consent.isPending ? 'Recording…' : 'Client consented to portfolio use'}
          </Button>
          <span className="muted" style={{ fontSize: 11.5, marginLeft: 8 }}>
            Records the client’s consent to use this set publicly (distinct from marketing
            messages).
          </span>
        </div>
      ) : null}
    </div>
  );
}

/** One half of a set (before OR after): the signed-URL thumbnail with a delete, or an "add" placeholder. */
function PhotoSlot({
  label,
  photo,
  onAdd,
  onDelete,
}: {
  label: string;
  photo: ClientPhoto | null;
  onAdd: () => void;
  onDelete: (photo: ClientPhoto) => void;
}) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="muted" style={{ fontSize: 11.5, fontWeight: 600 }}>
        {label}
      </span>
      {photo && photo.status === 'ready' && photo.downloadUrl ? (
        <div style={{ position: 'relative' }}>
          <img
            src={photo.downloadUrl}
            alt={`${label} photo`}
            style={{
              width: '100%',
              aspectRatio: '3 / 4',
              objectFit: 'cover',
              borderRadius: 8,
              border: '1px solid var(--slm-border)',
              display: 'block',
            }}
          />
          <button
            type="button"
            aria-label={`Delete ${label.toLowerCase()} photo`}
            onClick={() => onDelete(photo)}
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              border: 'none',
              borderRadius: 6,
              padding: '2px 6px',
              fontSize: 12,
              cursor: 'pointer',
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
            }}
          >
            Delete
          </button>
        </div>
      ) : photo && photo.status === 'pending' ? (
        <div
          style={{
            width: '100%',
            aspectRatio: '3 / 4',
            borderRadius: 8,
            border: '1px dashed var(--slm-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
          }}
          className="muted"
        >
          Uploading…
        </div>
      ) : (
        <button
          type="button"
          onClick={onAdd}
          className="muted"
          style={{
            width: '100%',
            aspectRatio: '3 / 4',
            borderRadius: 8,
            border: '1px dashed var(--slm-border)',
            background: 'none',
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          + Add {label.toLowerCase()}
        </button>
      )}
    </div>
  );
}

/**
 * Typed-confirm DELETE: the image is genuinely erased (the row dropped + the blob delete enqueued), not just
 * hidden — so it requires typing the client's name (or "delete") to arm, mirroring the WS6 destructive-action
 * pattern. The server enqueues the bucket delete; we surface a real error faithfully.
 */
function DeletePhotoDialog({
  clientId,
  photo,
  clientName,
  onClose,
}: {
  clientId: string;
  photo: ClientPhoto;
  clientName: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const del = useDeletePhoto();
  const phrase = clientName.trim() || 'delete';

  async function confirm() {
    try {
      await del.mutateAsync({ clientId, photoId: photo.id });
      toast.success('Photo deleted.');
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not delete this photo.');
    }
  }

  return (
    <ConfirmModal
      open
      title="Delete photo"
      confirmLabel={del.isPending ? 'Deleting…' : 'Delete photo'}
      confirmPhrase={phrase}
      busy={del.isPending}
      onConfirm={() => void confirm()}
      onClose={onClose}
    >
      <p style={{ margin: '0 0 8px' }}>
        This permanently deletes the {photo.kind === 'before' ? '“before”' : '“after”'} photo and
        removes the image from storage. This can’t be undone.
      </p>
      <p style={{ margin: 0 }}>
        If this set was on your public showcase, that likeness is removed too.
      </p>
    </ConfirmModal>
  );
}
